<template>
  <subscription-guard>
    <div class="ir-page">

      <!-- Header -->
      <div class="ir-header">
        <div>
          <h1 class="ir-title">Instagram report <span class="ir-beta">Beta</span></h1>
          <p class="ir-subtitle">Measure your results on content from Instagram.</p>
        </div>
        <button class="ir-filter-btn" @click="filtersTmp = cloneDeep(filters); filterDialog = true;">
          <i class="fal fa-sliders-h"></i>
          Filters
          <span v-if="filtersCount > 0" class="ir-filter-count">{{ filtersCount }}</span>
        </button>
      </div>

      <!-- Stats row -->
      <div class="ir-stats-card">
        <div class="ir-stat">
          <div class="ir-stat-icon"><i class="fal fa-bullseye"></i></div>
          <div>
            <div class="ir-stat-value">{{ summerizedStats.reach | local_numbers }}</div>
            <div class="ir-stat-label">Reach</div>
          </div>
        </div>
        <div class="ir-divider"></div>
        <div class="ir-stat">
          <div class="ir-stat-icon"><i class="fal fa-eye"></i></div>
          <div>
            <div class="ir-stat-value">{{ summerizedStats.impressions | local_numbers }}</div>
            <div class="ir-stat-label">Impressions</div>
          </div>
        </div>
        <div class="ir-divider"></div>
        <div class="ir-stat">
          <div class="ir-stat-icon"><i class="fal fa-users"></i></div>
          <div>
            <div class="ir-stat-value">{{ summerizedStats.influencers | local_numbers }}</div>
            <div class="ir-stat-label">Influencers</div>
          </div>
        </div>
        <div class="ir-divider"></div>
        <div class="ir-stat">
          <div class="ir-stat-icon"><i class="fal fa-plus-circle"></i></div>
          <div>
            <div class="ir-stat-value">{{ summerizedStats.story_posts | local_numbers }}</div>
            <div class="ir-stat-label">Story posts</div>
          </div>
        </div>
        <div class="ir-divider"></div>
        <div class="ir-stat">
          <div class="ir-stat-icon"><i class="fal fa-images"></i></div>
          <div>
            <div class="ir-stat-value">{{ summerizedStats.feed_posts | local_numbers }}</div>
            <div class="ir-stat-label">Feed posts</div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="ir-table-card">
        <div class="ir-table-header">
          <div>
            <div class="ir-table-title">By influencer</div>
            <div class="ir-table-subtitle">Instagram content performance this period</div>
          </div>
        </div>
        <instagram-report-data-table :items="influencerStats" />
      </div>

      <!-- Filter dialog -->
      <v-dialog v-model="filterDialog" max-width="480px">
        <v-card style="border-radius: 12px; padding: 8px;">
          <v-card-title style="font-size: 16px; font-weight: 700;">Filter report</v-card-title>
          <v-card-text>
            <date-picker
              v-if="filtersTmp.start_date"
              v-model="filtersTmp.start_date"
              label="Period start"
              cancel-button
              :reset-button="false"
            />
            <date-picker
              v-if="filtersTmp.end_date"
              v-model="filtersTmp.end_date"
              label="Period end"
              cancel-button
              :reset-button="false"
            />
          </v-card-text>
          <v-card-actions style="padding: 8px 16px 16px;">
            <v-spacer />
            <v-btn text @click="filterDialog = false">Close</v-btn>
            <v-btn color="#e05540" dark depressed style="border-radius: 8px;" @click="filters = JSON.parse(JSON.stringify(filtersTmp)); filterDialog = false;">
              Apply
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </div>
  </subscription-guard>
</template>

<script>
import { mapActions } from "vuex";
import search_filters_url from "@/helpers/mixins/search_filters_url";
import local_numbers from "@/helpers/filters/local_numbers";
import InstagramReportDataTable from "../../data-tables/InstagramReportDataTable";
import DatePicker from "../../common/filters/DatePicker";
import SubscriptionGuard from "@/components/business/pages/SubscriptionGuard";

export default {
  components: { InstagramReportDataTable, DatePicker, SubscriptionGuard },
  mixins: [search_filters_url],
  filters: { local_numbers },
  data() {
    return {
      summerizedStats: {},
      influencerStats: [],
      filterDialog: false,
      filtersTmp: {},
      filters: {}
    };
  },
  computed: {
    filtersCount() {
      return Object.keys(this.filters).length;
    }
  },
  watch: {
    filters: {
      handler() {
        this.setUrlSearchFilters(this.filters);
        this.getStats();
      },
      deep: true
    }
  },
  methods: {
    ...mapActions("core", ["getInstagramReportSummerized", "getInstagramReportInfluencers"]),
    getStats() {
      const params = {
        posted_start_date: this.filters.start_date,
        posted_end_date: this.filters.end_date,
      };
      this.getInstagramReportSummerized(params).then(r => { this.summerizedStats = r; });
      this.getInstagramReportInfluencers(params).then(r => { this.influencerStats = r.data; });
    }
  },
  created() {
    this.filters = this.getUrlSearchFilters();
    if (Object.keys(this.filters).length === 0) {
      this.filters.start_date = this.$moment().subtract(30, 'days').format('YYYY-MM-DD');
      this.filters.end_date = this.$moment().format('YYYY-MM-DD');
    }
  },
};
</script>

<style lang="scss" scoped>
.ir-page { max-width: 1160px; margin: 0 auto; padding: 0; }

.ir-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  margin-bottom: 24px; gap: 16px; flex-wrap: wrap;
}
.ir-title {
  font-size: 22px; font-weight: 700; color: var(--ink-1, #1a1a1a);
  display: flex; align-items: center; gap: 8px; margin: 0 0 4px;
}
.ir-beta {
  font-size: 10px; font-weight: 700; background: #fceae8; color: #e05540;
  border: 1px solid #f0d0ca; border-radius: 6px; padding: 1px 7px;
}
.ir-subtitle { font-size: 13px; color: var(--ink-3, #888); margin: 0; }

.ir-filter-btn {
  display: flex; align-items: center; gap: 6px;
  border: 1px solid var(--line, #e8e8e8); border-radius: 8px;
  padding: 7px 14px; font-size: 13px; font-weight: 600;
  color: var(--ink-2, #444); background: var(--surface, #fff);
  cursor: pointer; align-self: flex-end;
  &:hover { background: var(--surface-2, #f7f7f7); }
}
.ir-filter-count {
  background: #e05540; color: #fff;
  border-radius: 99px; font-size: 10px; font-weight: 700;
  padding: 1px 6px; margin-left: 2px;
}

/* Stats row */
.ir-stats-card {
  display: flex; align-items: center;
  background: var(--surface, #fff);
  border: 1px solid var(--line, #e8e8e8);
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  gap: 0;
  overflow-x: auto;
}
.ir-stat {
  display: flex; align-items: center; gap: 14px;
  flex: 1; min-width: 120px;
}
.ir-stat-icon {
  width: 40px; height: 40px; border-radius: 50%;
  background: #fceae8; color: #e05540;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; flex-shrink: 0;
}
.ir-stat-value { font-size: 20px; font-weight: 700; color: var(--ink-1, #1a1a1a); line-height: 1.2; }
.ir-stat-label { font-size: 12px; color: var(--ink-3, #888); margin-top: 2px; }
.ir-divider {
  width: 1px; background: var(--line, #e8e8e8);
  align-self: stretch; margin: 0 24px; flex-shrink: 0;
}

/* Table card */
.ir-table-card {
  background: var(--surface, #fff);
  border: 1px solid var(--line, #e8e8e8);
  border-radius: 12px;
  overflow: hidden;
}
.ir-table-header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--line, #e8e8e8);
}
.ir-table-title { font-size: 15px; font-weight: 700; color: var(--ink-1, #1a1a1a); }
.ir-table-subtitle { font-size: 12px; color: var(--ink-3, #888); margin-top: 2px; }
</style>